import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select/async";
import { Button } from "react-bootstrap";
import debounce from "lodash/debounce";
import { getUserList } from "../../../services/Users-services";
import { submitHostelApplication } from "../../../services/OrganisationServices/hostelManagmentService";
import { useGlobalState } from "../../../services/hostelConst";
import {
  callToast,
  showErrorMessages,
} from "../../../services/utilityFunctions";
import { components } from "react-select";

const AddHostelApplicationComponent = ({ navigate, refresh }) => {
  const { closeModal, modalState } = useGlobalState();
  const hostelId = modalState.id;
  const [formData, setFormData] = useState({
    email: "",
    mobile: "",
    userId: "",
    hostelId,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [defaultUserOptions, setDefaultUserOptions] = useState([]);

  // Modified useEffect
  useEffect(() => {
    const loadInitialUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const response = await getUserList({
          status: "Y",
          pageNum: 1,
          pageSize: 10,
        });
        console.log("Initial users response:", response);

        if (response.status === "success") {
          const entityList = response.data?.entityList;
          if (entityList && Array.isArray(entityList)) {
            const options = entityList.map((user) => ({
              value: user._ID,
              label: `${user["NAME"]} `,
              email: user.EMAIL || "",
              mobile: user.MOBILE || "",
            }));
            setDefaultUserOptions(options);
          }
        }
      } catch (error) {
        console.error("Error loading initial users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    loadInitialUsers();
  }, []);

  const loadUserOptions = useCallback(
    debounce(async (inputValue, callback) => {
      console.log("loadUserOptions called with:", inputValue);
      if (!inputValue || inputValue.length < 2) {
        callback(defaultUserOptions);
        return;
      }

      setIsLoadingUsers(true);
      try {
        const response = await getUserList({
          status: "Y",
          query: inputValue,
          pageNum: 1,
          pageSize: 10,
        });
        console.log("Search users response:", response);

        if (response.status === "success" && response.data?.entityList) {
          const options = response.data.entityList.map((user) => ({
            value: user._ID,
            label: `${user["NAME"]} (${user.USERNAME || "No Username"})`,
            email: user.EMAIL || "",
            mobile: user.MOBILE || "",
          }));
          callback(options);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error("Error loading users:", error);
        callback([]);
      } finally {
        setIsLoadingUsers(false);
      }
    }, 300),
    [defaultUserOptions]
  );
  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[0-9]{10}$/;

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.mobile?.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!mobileRegex.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }

    if (!formData.userId) {
      newErrors.userId = "Please select a user";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await submitHostelApplication(formData);
      console.log("Submit application response:", response);
      if (response.status === "success") {
        callToast("success", "Success", "Application submitted successfully!");
        await refresh?.({ columnRefresh: false, isLoading: false });
        closeModal?.();
      } else {
        showErrorMessages({ data: response, navigate });
      }
    } catch (error) {
      console.log("what is the error! man?? ", error);
      callToast("error", "Error", "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="">
      <div className="d-flex justify-content-center">
        <div className="w-100 m-3">
          <div className="d-flex h-100">
            <div className="w-100">
              <div className="stepper mb-3">
                <div className="d-flex justify-content-between">
                  <div className="step active">Application Info</div>
                </div>
              </div>

              <div className="mb-3 m-4">
                <label className="form-label">Select User</label>

                <Select
                  cacheOptions
                  defaultOptions={defaultUserOptions} // Changed to boolean true
                  value={selectedUser}
                  loadOptions={loadUserOptions}
                  onChange={(selected) => {
                    console.log("Selected user:", selected);
                    setSelectedUser(selected);
                    setFormData((prev) => ({
                      ...prev,
                      userId: selected?.value || "",
                      email: selected?.email || "",
                      mobile: selected?.mobile || "",
                    }));
                    setErrors({ ...errors, userId: "" });
                  }}
                  isSearchable
                  isLoading={isLoadingUsers}
                  placeholder="Search or select a user..."
                  className={errors.userId ? "is-invalid" : ""}
                  noOptionsMessage={({ inputValue }) =>
                    !inputValue
                      ? "Type to search users"
                      : inputValue.length < 2
                      ? "Type at least 2 characters to search"
                      : "No users found"
                  }
                  components={{
                    DropdownIndicator: (props) =>
                      isLoadingUsers ? (
                        <div className="px-2">
                          <div className="spinner-border text-primary spinner-border-sm" />
                        </div>
                      ) : (
                        <components.DropdownIndicator {...props} />
                      ),
                  }}
                  styles={{
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    control: (base) => ({
                      ...base,
                      minHeight: 40,
                      borderColor: errors.userId ? "#dc3545" : "#ced4da",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                  menuPortalTarget={document.body}
                />
                {errors.userId && (
                  <small className="text-danger">{errors.userId}</small>
                )}
              </div>

              <div className="mb-3 m-4">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder="Enter email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, email: e.target.value }));
                    setErrors({ ...errors, email: "" });
                  }}
                />
                {errors.email && (
                  <small className="text-danger">{errors.email}</small>
                )}
              </div>

              <div className="mb-3 m-4">
                <label className="form-label">Mobile Number</label>
                <input
                  type="tel"
                  className={`form-control ${
                    errors.mobile ? "is-invalid" : ""
                  }`}
                  placeholder="Enter 10-digit mobile number"
                  value={formData.mobile}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      mobile: e.target.value,
                    }));
                    setErrors({ ...errors, mobile: "" });
                  }}
                />
                {errors.mobile && (
                  <small className="text-danger">{errors.mobile}</small>
                )}
              </div>

              <div className="d-flex justify-content-end m-3 mb-0">
                <Button type="submit" disabled={loading} onClick={handleSubmit}>
                  {loading ? (
                    <div className="spinner-border text-light" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AddHostelApplicationComponent;
